import {Button, Modal, Space, Spin, Tag, Tooltip} from "antd";
import React, {FC, memo, useEffect, useState} from "react";
import {blue} from "@ant-design/colors";
import {EditOutlined} from "@ant-design/icons";
import EditRestrictiveBooking from "./EditRestrictiveBooking";
import {
    Course,
    Maybe,
    useGetRestrictiveBookingsByCourseIdQuery,
} from "../../../generated/graphql";
import PreviewSmall from "./PreviewSmall";
import CourseIsPrerequisiteForCourses from "./CourseIsPrerequisiteForCourses";
import {deriveBookingRestrictionActive} from "../../../../shared/bookingRestriction/deriveBookingRestrictionActive";

type EditRestrictiveBookingModalProps = {
    courseId: Maybe<Course["id"]> | undefined;
    prefixedCourseNumber?: string;
    tooltipText?: string;
    refetch?: () => void;
    initialOpen?: boolean;
    onSetOpenState?: (open: boolean) => void;
};

const EditRestrictiveBookingModal: FC<EditRestrictiveBookingModalProps> = ({
    courseId,
    prefixedCourseNumber,
    tooltipText,
    refetch,
    initialOpen = false,
    onSetOpenState,
}) => {
    const [open, setOpen] = useState(initialOpen);
    const [active, setActive] = useState(false);

    React.useEffect(() => {
        onSetOpenState?.(open);
    }, [onSetOpenState, open]);

    const {data, loading} = useGetRestrictiveBookingsByCourseIdQuery({
        skip: !courseId,
        variables: {
            courseId: courseId ?? "",
        },
        fetchPolicy: "network-only",
    });
    const existingRestrictiveBooking = data?.getBookingRestrictionByCourseId;
    const persistedActive = existingRestrictiveBooking
        ? deriveBookingRestrictionActive({
              activeWithoutTimeFrame: existingRestrictiveBooking.active,
              startDateTime: existingRestrictiveBooking.startDateTime,
              endDateTime: existingRestrictiveBooking.endDateTime,
              membersOnly: existingRestrictiveBooking.membersOnly,
              hasPrerequisiteCourses:
                  (existingRestrictiveBooking.prerequisiteCourses?.length ??
                      0) > 0,
              expireAction: existingRestrictiveBooking.expireAction,
          })
        : false;

    useEffect(() => {
        if (active !== persistedActive) {
            setActive(persistedActive);
        }
    }, [active, persistedActive]);

    const ExtraTag = ({active}: {active: boolean}) => {
        const text = active ? "Aktiv" : "Inaktiv";

        return (
            <Tag
                color={active ? "green" : "red"}
                style={{borderRadius: "11px", height: "21px"}}
            >
                {text}
            </Tag>
        );
    };

    if (!open) {
        return (
            <Tooltip title={tooltipText}>
                <div
                    style={{
                        border: `1px solid lightgrey`,
                        borderRadius: 6,
                        padding: 3,
                    }}
                >
                    <Button
                        onClick={() => setOpen(true)}
                        style={{
                            width: "100%",
                            color: blue[5],
                            borderColor: blue[3],
                        }}
                        disabled={!courseId}
                    >
                        <Space>
                            <EditOutlined />
                            {"Buchungsbeschränkung bearbeiten"}
                            <ExtraTag active={active} />
                        </Space>
                    </Button>
                    <PreviewSmall
                        courseId={courseId}
                        existingRestrictiveBooking={existingRestrictiveBooking}
                    />
                    <CourseIsPrerequisiteForCourses
                        courseId={courseId}
                        prefixedCourseNumber={prefixedCourseNumber}
                    />
                </div>
            </Tooltip>
        );
    }

    if (loading) {
        return <Spin />;
    }

    return (
        <Modal
            title={
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        paddingRight: 20,
                    }}
                >
                    {prefixedCourseNumber ? (
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "row",
                                flexWrap: "wrap",
                                gap: 5,
                            }}
                        >
                            <span
                                style={{whiteSpace: "nowrap"}}
                            >{`Buchungsbeschränkung bearbeiten `}</span>
                            <span
                                style={{whiteSpace: "nowrap"}}
                            >{`(${prefixedCourseNumber})`}</span>
                        </div>
                    ) : (
                        <div>{"Buchungsbeschränkung bearbeiten"}</div>
                    )}
                    <ExtraTag active={active} />
                </div>
            }
            open={open}
            onCancel={() => setOpen(false)}
            footer={null}
            width={600}
            bodyStyle={{padding: 0}}
            destroyOnClose
        >
            <EditRestrictiveBooking
                courseId={courseId}
                setOpen={setOpen}
                setActive={setActive}
                existingRestrictiveBooking={existingRestrictiveBooking}
                refetch={refetch}
            />
        </Modal>
    );
};

export default memo(EditRestrictiveBookingModal);
